import { trackEvent } from 'lib/posthog';

let hasStarted = false;

const round = (n) =>
  typeof n === 'number' ? Math.round(n * 10000) / 10000 : null;

/**
 * Splits "the attributed element grew" from "it was pushed by something above".
 *
 * `largestShiftTarget` names the element that moved, which is not necessarily
 * the one that caused the move. Comparing the source's rects settles it: a
 * changed `top` with an unchanged `height` means the element was displaced and
 * the cause sits above it; a grown `height` means the element is the cause.
 *
 * Reads `largestShiftSource` rather than walking `largestShiftEntry.sources`
 * on purpose. Its node is the same one web-vitals resolves into
 * `largestShiftTarget`, so the selector already in the event identifies these
 * deltas. The library's selector generator is not a public export, so any
 * other source would carry an identifier in a different format.
 *
 * A `LayoutShift` can be attributed with no source, and a source's node is
 * null once the element leaves the DOM. Both yield nulls rather than a throw:
 * the rects survive a null node, but `largestShiftTarget` comes from that same
 * node, so the deltas would describe an element nothing can name.
 *
 * The three fields are one atomic block: they are emitted together or nulled
 * together, so a sample is either wholly attributable or wholly absent. A row
 * carrying a count but no deltas reads as "shift with sources we chose not to
 * measure", which is not a state that exists — the Fase 2 gate filters on the
 * three together. Do not lift `sources_count` out of here to compute it
 * independently of the source; that is exactly the split this replaced.
 */
const attributableShiftFields = (attribution) => {
  const source = attribution.largestShiftSource;
  const previous = source?.previousRect;
  const current = source?.currentRect;

  if (!source?.node || !previous || !current) {
    return {
      shift_delta_top: null,
      shift_delta_height: null,
      sources_count: null,
    };
  }

  return {
    shift_delta_top: round(current.top - previous.top),
    shift_delta_height: round(current.height - previous.height),
    sources_count: attribution.largestShiftEntry?.sources?.length ?? null,
  };
};

/**
 * Reports to Sentry without ever rejecting.
 *
 * Both callers run where a rejection would be invisible: a catch handler and
 * an unload-time callback. If Sentry itself cannot load there is nowhere left
 * to report, so the failure is logged and dropped rather than surfacing as an
 * unhandled rejection.
 */
const captureError = (error) =>
  import('@sentry/nextjs')
    .then(({ captureException }) => {
      captureException(error);
    })
    .catch(() => {
      // eslint-disable-next-line no-console
      console.warn('[webVitals] could not report CLS failure to Sentry', error);
    });

/**
 * Reports the page's final CLS score plus the element that shifted most.
 *
 * CrUX exposes only an aggregate p75 with no attribution, and the shift
 * does not reproduce in a lab session, so `largestShiftTarget` gathered
 * from real visits is the only way to identify which element moves.
 *
 * Uses the `try` nature: the taxonomy defines it for system events with
 * no direct user interaction, which is what a performance measurement is.
 * `source_type: 'system'` marks it as not user-initiated.
 *
 * `onCLS` reports once per page visit, when the page is backgrounded or
 * unloaded, and keeps accumulating across soft navigations rather than
 * resetting. The module-level guard matches that lifetime: re-arming on
 * every route change would register duplicate listeners for one visit.
 *
 * Returns the import promise so tests can await registration deterministically.
 * Callers in render code ignore it.
 *
 * @returns {Promise<void>}
 */
const reportCls = () => {
  if (typeof window === 'undefined' || hasStarted) return Promise.resolve();

  hasStarted = true;

  // `onCLS` observes layout-shift with `buffered: true`, so registering after
  // a soft navigation replays shifts from the route the visit started on
  //
  // There is no API to flush that buffer, so record where measurement was
  // armed and drop contaminated samples at analysis time. PostHog attaches
  // the report-time URL as `$current_url`, so only the armed path is needed
  const armedPath = window.location.pathname;

  return import('web-vitals/attribution')
    .then(({ onCLS }) => {
      onCLS(({ value, rating, navigationType, attribution }) => {
        // onCLS fires as the page is being backgrounded or unloaded, where a
        // throw would be swallowed by the browser. Diagnostic instrumentation
        // must never be the reason a visit ends badly
        try {
          trackEvent('try web vital cls', {
            source_type: 'system',
            status: 'success',
            value: round(value),
            rating,
            navigation_type: navigationType,
            // Names the element that moved, not the one that caused the
            // move. Pair it with shift_time to infer what appeared above
            target: attribution.largestShiftTarget ?? null,
            shift_value: round(attribution.largestShiftValue),
            // Separates markup rendering late (early ms) from async content
            // such as images or third parties (seconds in)
            shift_time:
              typeof attribution.largestShiftTime === 'number'
                ? Math.round(attribution.largestShiftTime)
                : null,
            load_state: attribution.loadState ?? null,
            armed_path: armedPath,
            ...attributableShiftFields(attribution),
          });
        } catch (error) {
          captureError(error);
        }
      });
    })
    .catch(captureError);
};

export default reportCls;
